import type React from 'react'
import { useIntl } from 'react-intl'

import { Link } from '@tanstack/react-router'

import { AnimatedBox } from '@/components/atoms/AnimatedBox'
import { Car, type CarProps } from '@/components/atoms/Car/Car.tsx'
import { Icons } from '@/components/atoms/Icons/Icons'
import { MessageImage } from '@/components/molecules/MessageImage'
import { VoiceClipPlayer } from '@/components/molecules/VoiceClipPlayer'

export interface TeamMessageCardProps {
  sender: 'volunteer' | 'admin'
  volunteerRole?: 'driver' | 'partner' | 'escort'
  senderName?: string
  createdAt: Date
  readAt?: Date
  regionName: string
  piloteRegionId?: number
  text?: string
  firebaseStoragePath?: string
  imageHeight?: number
  imageWidth?: number
  audioStoragePath?: string
  audioDurationMs?: number
  isBroadcast?: boolean
  car?: Pick<CarProps, 'state' | 'carNumber' | 'carType'>
  transportRequest?: {
    _id: string
    piloteId: string
    address?: string
    status?: 'allocated' | 'assigned' | 'on_board'
  }
}

export const TeamMessageCard: React.FC<TeamMessageCardProps> = ({
  sender,
  volunteerRole,
  senderName,
  createdAt,
  readAt,
  text,
  firebaseStoragePath,
  imageHeight,
  imageWidth,
  audioStoragePath,
  audioDurationMs,
  isBroadcast,
  car,
  transportRequest,
  regionName,
  piloteRegionId,
}) => {
  const intl = useIntl()
  return (
    <AnimatedBox>
      <div className="flex flex-col text-base">
        {transportRequest && (
          <div className="flex items-center gap-4 border-t border-gray-400 bg-gray-100 px-6 py-2 select-none">
            <div className="flex items-center gap-0.5 text-blue-700 uppercase">
              <span>#</span>
              <Link to="/" search={{ transportRequestId: transportRequest._id, central_id: piloteRegionId }}>
                {transportRequest.piloteId}
              </Link>
            </div>
            <span className="text-gray-500">&gt;</span>
            <div className="flex items-center gap-1 text-gray-500">
              {transportRequest.status === 'allocated' && <Icons.pinSmall className="size-4 fill-blue-500" />}
              {transportRequest.status === 'assigned' && <Icons.pinSmall className="size-4 fill-blue-500" />}
              {transportRequest.status === 'on_board' && <Icons.flagSmall className="size-4 fill-green-500" />}
              <p className="self-start">{transportRequest.address}</p>
            </div>
          </div>
        )}
        <div
          className={`grid grid-cols-[6rem_1fr] items-baseline gap-1 border-t ${isBroadcast ? 'border-gray-300 bg-orange-50' : 'border-gray-200'} py-5 text-base`}>
          <p className="justify-self-center text-sm text-gray-900 select-none">{intl.formatTime(createdAt)}</p>
          <div className="select-none">
            <span className="font-bold">
              {sender === 'volunteer'
                ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  intl.formatMessage({ id: `message.team.${volunteerRole!}` }, { name: senderName })
                : isBroadcast
                  ? intl.formatMessage({ id: 'message.broadcast.label' })
                  : intl.formatMessage({ id: 'message.region' }, { region: regionName })}
            </span>
            {readAt && (
              <span className="ml-4 font-bold text-blue-500">
                {sender === 'volunteer'
                  ? intl.formatMessage({ id: 'message.confirmed.byTeam' }, { time: intl.formatTime(readAt) })
                  : intl.formatMessage({ id: 'message.confirmed.read' }, { time: intl.formatTime(readAt) })}
              </span>
            )}
          </div>
          <div className="justify-self-center">
            {sender === 'volunteer' ? (
              car && <Car {...car} />
            ) : isBroadcast ? (
              <Icons.megaphone className="text-gray-800" />
            ) : (
              <Icons.ONR />
            )}
          </div>
          <div className="flex w-full flex-col gap-3 self-start">
            {text && <div className="text-lg">{text}</div>}
            {audioStoragePath && <VoiceClipPlayer storagePath={audioStoragePath} durationMs={audioDurationMs} />}
            {firebaseStoragePath && (
              <MessageImage storagePath={firebaseStoragePath} width={imageWidth} height={imageHeight} />
            )}
          </div>
        </div>
      </div>
    </AnimatedBox>
  )
}
